import { useSyncExternalStore } from 'react'
import { collection, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore'
import { adminDb } from '../../../../packages/firebase/adminClient'
import { normalizeReportStatus } from '../../../../packages/validation/admin'

const MAX_REPORTS = 500
const EMPTY_STATE = { items: [], ready: false, error: null, newCount: 0, countReady: false, countError: null }

let state = EMPTY_STATE
let unsubscribeList = null
let unsubscribeCount = null
const listeners = new Set()

export function isNewReport(report) {
  return normalizeReportStatus(report?.status) === 'new'
}

export function countNewReports(reports) {
  return (reports || []).filter(isNewReport).length
}

export function formatReportBadgeCount(count) {
  return count > 99 ? '99+' : String(count)
}

function emit(nextState) {
  state = nextState
  listeners.forEach(listener => listener())
}

function start() {
  if (unsubscribeList || unsubscribeCount) return
  if (!adminDb) {
    const error = new Error('Firestore가 초기화되지 않았습니다.')
    emit({ ...state, ready: true, error, countReady: true, countError: error })
    return
  }
  const reportsQuery = query(collection(adminDb, 'feedbackReports'), orderBy('createdAt', 'desc'), limit(MAX_REPORTS))
  unsubscribeList = onSnapshot(reportsQuery, snapshot => {
    emit({ ...state, items: snapshot.docs.map(item => ({ id: item.id, ...item.data() })), ready: true, error: null })
  }, error => {
    // 목록 오류는 마지막 정상 목록을 보존한다. badge 오류와 독립적으로 처리한다.
    emit({ ...state, ready: true, error })
  })
  // Badge는 전체 목록을 다운로드하지 않고 NEW 후보 상태만 실시간 구독한다.
  // status 없는 legacy 문서는 이 query에 포함되지 않으므로 기존 fallback 정책도 유지된다.
  const newReportsQuery = query(collection(adminDb, 'feedbackReports'), where('status', 'in', ['pending', 'new']))
  unsubscribeCount = onSnapshot(newReportsQuery, snapshot => {
    emit({ ...state, newCount: snapshot.docs.filter(item => isNewReport(item.data())).length, countReady: true, countError: null })
  }, error => {
    // count 실패 시 stale badge를 표시하지 않도록 countError만 세운다.
    emit({ ...state, countReady: true, countError: error })
  })
}

function stop() {
  if (listeners.size || (!unsubscribeList && !unsubscribeCount)) return
  unsubscribeList?.()
  unsubscribeCount?.()
  unsubscribeList = null
  unsubscribeCount = null
  state = EMPTY_STATE
}

export function subscribeReports(listener) {
  listeners.add(listener)
  start()
  return () => {
    listeners.delete(listener)
    stop()
  }
}

export function getReportsSnapshot() {
  return state
}

export function useReportTriage() {
  return useSyncExternalStore(subscribeReports, getReportsSnapshot, getReportsSnapshot)
}
